import {useEffect} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {socket} from '@/socket'
import {QUERY_KEYS} from '@/queryClient'
import {NotificationCount, SocketNotification, UserNotification} from '../types'
import {addNotificationToCache} from '../utils/cacheUtils'

export const useSocketNotifications = (userId: string) => {
    const queryClient = useQueryClient()

    useEffect(() => {
        if (!userId) return
        // Join the user-specific room
        socket.emit('join', `users:${userId}`)

        const intervalId = setInterval(() => {
            socket.emit('ping')
        }, 60000)

        socket.once('pong', (data: {sessionEnabled: boolean}) => {
            if (!data.sessionEnabled) {
                console.log('Socket session disabled:', data)
            }
        })

        // Handle incoming notifications
        socket.on('notification', (rawSocketData: unknown) => {
            try {
                // Validate the incoming notification data
                const parseResult = SocketNotification.safeParse(rawSocketData)

                if (!parseResult.success) {
                    console.error('Invalid notification data received:', parseResult.error)
                    return
                }

                const socketData = parseResult.data

                // Get content from either bodyTemplate or body field
                const content = socketData.bodyTemplate || socketData.body || ''

                // Create a UserNotification object to match the format used in the queries
                const userNotification: UserNotification = {
                    id: socketData.id,
                    userId: userId,
                    notificationId: socketData.id,
                    conversationId: null,
                    readAt: null,
                    details: null,
                    notification: {
                        id: socketData.id,
                        title: socketData.title,
                        bodyTemplate: content,
                        details: null,
                        type: socketData.type,
                        severity: socketData.severity,
                        link: socketData.link,
                        triggeredBy: socketData.triggeredBy || 'system',
                        createdAt: socketData.createdAt,
                        updatedAt: socketData.createdAt,
                        scheduledAt: null,
                        sentAt: socketData.sentAt || socketData.createdAt,
                        extraData: socketData.extraData || null,
                        triggeredByUser: {
                            id: socketData.triggeredBy || 'system',
                            firstName: 'System',
                            lastName: ''
                        }
                    }
                }

                // Optimistically update notification count
                queryClient.setQueryData<NotificationCount>([QUERY_KEYS.NOTIFICATION_COUNT], oldData => {
                    if (!oldData) return {toRead: 1, total: 1}
                    return {
                        toRead: oldData.toRead + 1,
                        total: oldData.total + 1
                    }
                })
                // Add the new notification to all cached queries
                addNotificationToCache(queryClient, userNotification)
            } catch (error) {
                console.error('Error processing notification:', error)
            }
        })

        return () => {
            socket.emit('leave', `users:${userId}`)
            socket.off('pong')
            socket.off('notification')
            clearInterval(intervalId)
        }
    }, [userId, queryClient])
}
